import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getSocket } from '../../services/socket'
import { sounds } from '../../services/sounds'
import { useToastStore, type ToastItem, type ToastReplyTarget } from '../../stores/toastStore'
import { useAuthStore } from '../../stores/authStore'
import { useDmCallStore } from '@os/messenger/web/stores/dmCallStore'
import { useVoiceStore } from '../../stores/voiceStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { dmApi, messageApi } from '../../services/api'
import { Phone, PhoneOff, MessageCircle, X, AlertCircle, Send, Check } from 'lucide-react'
import EmojiText from '../chat/EmojiText'
import {
  showMessageNotification,
  showCallNotification,
  dismissCallNotification,
} from '../../services/notifications'
import { telephonyApi } from '../../services/telephony'
import { formatPhoneDisplay } from '../../utils/formatPhone'

function formatName(user: any) {
  if (user?.firstName) {
    return `${user.firstName} ${user.lastName || ''}`.trim()
  }
  return user?.username || 'User'
}

// OSPROD-269: inline quick-reply row inside a message toast. The target
// payload is bound to the toast at CREATION time (see ToastReplyTarget in
// stores/toastStore.ts), so a stack of simultaneous toasts can never
// cross destinations — each input closes over its own toast's target.
// The send rides the SAME REST endpoints the chat composers use
// (pendingSend fireRest → dmApi.send / messageApi.send). Both are
// id-addressed: the DM route authorises by DM membership, the channel
// route resolves the server from the channel row — neither scopes by the
// X-Server-Id header, so a reply to a room on ANOTHER server than the
// active one still lands in exactly the source chat.
function ToastQuickReply({
  toastId,
  target,
  onHoldChange,
}: {
  toastId: string
  target: ToastReplyTarget
  onHoldChange: (hold: boolean) => void
}) {
  const hideToast = useToastStore((s) => s.hideToast)
  const [text, setText] = useState('')
  const [sendState, setSendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')

  const send = async () => {
    const content = text.trim()
    if (!content || sendState === 'sending' || sendState === 'sent') return
    setSendState('sending')
    // A send in flight must never be auto-dismissed out from under the
    // confirmation — hold stays on through 'sent'/'error' too.
    onHoldChange(true)
    try {
      if (target.kind === 'dm') {
        await dmApi.send(target.dmChannelId, { content })
      } else {
        await messageApi.send({ channelId: target.channelId, content })
      }
      setSendState('sent')
      // Brief "Sent" confirmation, then the toast dismisses itself — the
      // user stays wherever they are (no navigation on reply).
      setTimeout(() => hideToast(toastId), 1200)
    } catch (err: unknown) {
      console.warn('[toast] quick reply send failed:', err)
      setSendState('error')
    }
  }

  if (sendState === 'sent') {
    return (
      <div
        className="mt-2 flex items-center gap-1.5 text-xs text-primary"
        onClick={(e) => e.stopPropagation()}
      >
        <Check size={14} />
        <span>Sent</span>
      </div>
    )
  }

  return (
    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-1.5">
        <input
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            if (sendState === 'error') setSendState('idle')
          }}
          onFocus={() => onHoldChange(true)}
          onBlur={() => {
            // Resume the dismiss timer only when nothing is typed and no
            // send is pending — an abandoned empty input shouldn't pin the
            // toast forever.
            if (!text.trim() && sendState !== 'sending') onHoldChange(false)
          }}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Enter') {
              e.preventDefault()
              void send()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              hideToast(toastId)
            }
          }}
          placeholder="Reply…"
          disabled={sendState === 'sending'}
          className="flex-1 min-w-0 bg-muted text-foreground text-xs rounded-md px-2 py-1.5 outline-none ring-1 ring-border focus:ring-primary/50 placeholder:text-muted-foreground disabled:opacity-60"
        />
        <button
          onClick={() => void send()}
          disabled={!text.trim() || sendState === 'sending'}
          className="w-7 h-7 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-default flex items-center justify-center transition shrink-0"
          title="Send"
        >
          <Send size={13} />
        </button>
      </div>
      {sendState === 'error' && (
        <div className="mt-1 text-[11px] text-destructive">Failed to send — try again</div>
      )}
    </div>
  )
}

function ToastItemComponent({ toast }: { toast: ToastItem }) {
  const navigate = useNavigate()
  const hideToast = useToastStore((s) => s.hideToast)
  const setCallState = useDmCallStore((s) => s.setCallState)
  // OSPROD-269: while the quick-reply input is focused / non-empty / mid-
  // send, the auto-dismiss timer below is paused so the toast can't
  // vanish under the user's cursor. Per-toast state — one held toast
  // doesn't pin its neighbours.
  const [replyHold, setReplyHold] = useState(false)

  // Auto-hide message + error toasts after 30s. Calls don't auto-dismiss —
  // they close on accept/decline/end via the container's socket handlers.
  // (Moved from the container-level effect so each toast can pause its own
  // timer independently for the quick reply.)
  useEffect(() => {
    if (toast.type === 'call') return
    if (replyHold) return
    const delay = Math.max(30000 - (Date.now() - toast.createdAt), 0)
    const t = setTimeout(() => hideToast(toast.id), delay)
    return () => clearTimeout(t)
  }, [toast.id, toast.type, toast.createdAt, replyHold, hideToast])

  const handleClick = () => {
    if (toast.phoneWaiting) {
      // Look at the channel (and its lobby strip) WITHOUT deciding — keep
      // the toast alive: the caller is still waiting. Admit/Decline or any
      // host's resolution dismisses it.
      if (toast.phoneWaiting.serverId) {
        navigate(`/servers/${toast.phoneWaiting.serverId}/channels/${toast.phoneWaiting.channelId}`)
      }
      return
    }
    if (toast.dmChannelId) {
      navigate(`/channels/dm-${toast.dmChannelId}`)
    }
    hideToast(toast.id)
  }

  const handleAcceptCall = (e: React.MouseEvent) => {
    e.stopPropagation()
    hideToast(toast.id)
    sounds.stopRingtone()

    // Unified phone lobby: admit the PSTN caller waiting in a voice channel.
    // Admit = answer the phone — the SIP leg only answers once it can
    // subscribe to a live audio track, so we must end up in the channel
    // with the mic published: unmute in place when already connected, else
    // park the one-shot 'unmute' auto-join intent and navigate there.
    if (toast.phoneWaiting) {
      const { channelId, serverId, waitingId } = toast.phoneWaiting
      getSocket().emit('voice:waiting:approve', { channelId, waitingId })
      // Only navigate into servers we're actually a member of — a global
      // admin can receive (and approve) requests for servers outside their
      // membership, where /servers/<id>/... dead-ends and the parked
      // auto-join intent would never be consumed.
      const isMember = !!serverId && useServerStore.getState().servers.some((s) => s.id === serverId)
      if (useVoiceStore.getState().currentChannelId === channelId) {
        useAuthStore.getState().unmuteForPhoneAdmit()
      } else if (isMember) {
        useVoiceStore.getState().setPendingAutoJoin(channelId, 'unmute')
        navigate(`/servers/${serverId}/channels/${channelId}`)
      } else {
        // Approved but no route to auto-join (missing serverId / not a
        // member) — don't park a stale auto-join intent. The caller still
        // needs someone in the room with a live mic to be answered.
        console.warn('[telephony] phone-waiting admit without a joinable route — join the channel manually')
      }
      return
    }

    // OSPROD-88: PSTN call accept — flip the server session to CONNECTED
    // (disarms the 30s missed-timeout), then join the LiveKit room that
    // livekit-sip already parked the SIP participant in. No socket
    // dm:call:accept needed (there is no internal user on the other side).
    // NOTE: dmCallStore.acceptIncomingPhoneCall (@os/messenger) runs this
    // same protocol for the mobile DmCallSplash + in-thread overlay off the
    // ring-time stash — if the sequence changes here, change it there too.
    // This toast deliberately keeps its own per-toast ids instead of the
    // single-slot stash so accepting still works after a superseding ring.
    if (toast.phoneCallRoom && toast.dmChannelId) {
      const username = useAuthStore.getState().user?.username
      if (username) {
        if (toast.phoneCallSessionId) {
          telephonyApi.calls.accept(toast.phoneCallSessionId)
            .then((acceptRes) => {
              // Seed the PSTN transcription anchors from the REST response —
              // the dm:call:transcription socket event fires during the
              // accept request, before the overlay mounts a listener; the
              // store survives overlay remounts. Skip if the call was
              // already torn down (or replaced) while the request was in
              // flight — don't plant stale anchors into an idle store.
              if (useDmCallStore.getState().phoneCallSessionId !== toast.phoneCallSessionId) return
              useDmCallStore.getState().setPhoneCallInfo({
                transcriptSessionId: acceptRes?.transcriptSessionId ?? null,
                phoneNumber: toast.phoneCallerNumber ?? null,
              })
            })
            .catch((err) => console.warn('[telephony] accept signal failed', err))
        }
        useDmCallStore.getState().setPhoneCallSessionId(toast.phoneCallSessionId ?? null)
        useDmCallStore.getState().setCallState('connected', {
          dmId: toast.dmChannelId,
          callType: 'audio',
          callerName: toast.title,
        })
        useDmCallStore.getState().connect(toast.phoneCallRoom, username).catch((err) => {
          console.warn('[telephony] phone accept connect failed', err)
          useDmCallStore.getState().setCallState('idle')
        })
        navigate(`/channels/dm-${toast.dmChannelId}`)
      }
      return
    }

    if (toast.dmChannelId && toast.callerId) {
      setCallState('connected', { dmId: toast.dmChannelId, callType: toast.callType || 'audio' })
      getSocket().emit('dm:call:accept', {
        dmChannelId: toast.dmChannelId,
        callerId: toast.callerId,
      })
      navigate(`/channels/dm-${toast.dmChannelId}`)
    }
  }

  const handleDeclineCall = (e: React.MouseEvent) => {
    e.stopPropagation()
    hideToast(toast.id)
    sounds.stopRingtone()

    // Unified phone lobby: reject the waiting PSTN caller (hangs up the
    // Twilio leg server-side and resolves the entry for every host).
    if (toast.phoneWaiting) {
      getSocket().emit('voice:waiting:reject', {
        channelId: toast.phoneWaiting.channelId,
        waitingId: toast.phoneWaiting.waitingId,
      })
      return
    }

    // PSTN DM decline → /calls/:id/end. The USER-scope /end branch kicks
    // the SIP participant from the dm-call room (livekit-sip BYEs the
    // Twilio leg) and finalizes the RINGING session as MISSED — its
    // phone:call:status emit dismisses the ring on the user's other
    // tabs/devices. (The old /calls/:id/reject route was ROOM-scope and
    // host-gated — it always 403'd these USER-scope sessions — and was
    // retired with the unified phone lobby.)
    if (toast.phoneCallSessionId) {
      telephonyApi.calls.end(toast.phoneCallSessionId)
        .catch((err) => console.warn('[telephony] phone decline failed', err))
      // handlePhoneIncoming parked this tab on the incoming splash state —
      // return it to idle. Guarded so a call accepted while the toast was
      // up isn't clobbered (phoneCallSessionId is only set on accept/dial,
      // never while ringing).
      const st = useDmCallStore.getState()
      if (st.callState === 'incoming' && st.callerId === 'phone') {
        st.setCallState('idle')
      }
      return
    }

    if (toast.dmChannelId && toast.callerId) {
      setCallState('idle')
      getSocket().emit('dm:call:decline', {
        dmChannelId: toast.dmChannelId,
        callerId: toast.callerId,
      })
    }
  }

  const isCall = toast.type === 'call'
  const isError = toast.type === 'error'

  return (
    <div
      onClick={handleClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border cursor-pointer transition-all duration-200 hover:scale-[1.02] min-w-[280px] max-w-[360px] ${
        isCall
          ? 'bg-card border-primary/40'
          : isError
          ? 'bg-card border-red-500/40'
          : 'bg-card border-white/10'
      }`}
    >
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
          isError ? 'bg-red-500/20 text-red-500' : 'bg-primary/20 text-primary'
        }`}
      >
        {isCall ? <Phone size={18} /> : isError ? <AlertCircle size={18} /> : <MessageCircle size={18} />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-foreground text-sm font-medium truncate">{toast.title}</div>
        {toast.senderName && (
          // OSPROD-152: sender nickname on its own line (between room title
          // and message preview) so it never gets truncated away.
          <div className="text-foreground/90 text-xs font-semibold truncate">{toast.senderName}</div>
        )}
        {toast.body && (
          // EmojiText converts standard unicode emoji into the same Apple/Tg
          // sticker images we use everywhere else in the app, so a DM
          // notification doesn't suddenly fall back to native OS emojis
          // that look out of place against the Telegram-style sticker set.
          <div className="text-muted-foreground text-xs truncate">
            <EmojiText text={toast.body} />
          </div>
        )}
        {/* Event time — every toast carries createdAt; surfacing it makes
            toasts more informative (when did this happen). */}
        <div className="text-[10px] text-muted-foreground/60 mt-1 tabular-nums">
          {new Date(toast.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
        {/* OSPROD-269: inline quick reply — only on message toasts whose
            creator bound a reply target. Clicks inside stop propagation,
            so the card's click-to-navigate stays on the rest of the
            surface. */}
        {!isCall && toast.replyTarget && (
          <ToastQuickReply
            toastId={toast.id}
            target={toast.replyTarget}
            onHoldChange={setReplyHold}
          />
        )}
      </div>

      {isCall && (
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={handleDeclineCall}
            className="w-8 h-8 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500 hover:text-foreground flex items-center justify-center transition"
          >
            <PhoneOff size={14} />
          </button>
          <button
            onClick={handleAcceptCall}
            className="w-8 h-8 rounded-full bg-green-500/20 text-green-500 hover:bg-green-500 hover:text-foreground flex items-center justify-center transition"
          >
            <Phone size={14} />
          </button>
        </div>
      )}

      {!isCall && toast.actionLabel && toast.onAction && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            try { toast.onAction?.() } catch (err: unknown) { console.warn('[toast] action handler threw:', err) }
            hideToast(toast.id)
          }}
          className="px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition shrink-0"
        >
          {toast.actionLabel}
        </button>
      )}

      {!isCall && (
        <button
          onClick={(e) => { e.stopPropagation(); hideToast(toast.id) }}
          className="text-muted-foreground hover:text-foreground transition shrink-0"
        >
          <X size={14} />
        </button>
      )}
    </div>
  )
}

export default function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts)
  const showToast = useToastStore((s) => s.showToast)
  const hideToast = useToastStore((s) => s.hideToast)
  const currentUser = useAuthStore((s) => s.user)
  const setCallState = useDmCallStore((s) => s.setCallState)

  useEffect(() => {
    const socket = getSocket()

    const handleDmMessage = ({ dmChannelId, message }: { dmChannelId: string; message: any }) => {
      if (message.authorId === currentUser?.id) return
      // Don't toast if currently viewing this DM
      const path = window.location.pathname
      if (path === `/channels/dm-${dmChannelId}`) return

      const authorName = formatName(message.author)
      const body = message.content || (message.photoUrl ? 'Photo' : message.videoUrl ? 'Video' : message.audioUrl ? 'Voice message' : 'Message')
      // In Electron, the OS notification is the primary surface — we
      // skip the in-app toast so users don't see a stack of corner
      // cards inside the window AND a Windows toast at the same time
      // (Telegram-style: tray-area popup, not in-app toast). Browser
      // users still get the corner toast since OS-level notifications
      // are blocked / muted on most desktops by default.
      const isElectron = typeof window !== 'undefined' && window.location.protocol === 'file:'
      if (!isElectron) {
        showToast({
          id: `msg-${message.id}`,
          type: 'message',
          title: authorName,
          body,
          dmChannelId,
          // OSPROD-269: bind the reply destination to THIS toast at
          // creation — the quick-reply input sends to exactly this DM.
          replyTarget: { kind: 'dm', dmChannelId },
        })
      }
      // Native OS notification — Electron forwards to Windows Action
      // Center / macOS Notification Center / Linux libnotify. The helper
      // self-suppresses when the page is focused on this DM, so we don't
      // double-notify when the user is mid-conversation. Passing
      // `messageId` makes the click route land on the exact bubble.
      showMessageNotification({ title: authorName, body, dmChannelId, messageId: message.id })
    }

    const handleIncomingCall = (data: any) => {
      if (data.callerId === currentUser?.id) return
      const path = window.location.pathname
      const inThisDm = path === `/channels/dm-${data.dmChannelId}`
      const isElectronEnv = typeof window !== 'undefined' && (
        window.location.protocol === 'file:' ||
        !!(window as any).electronAPI
      )

      // Busy-signal short-circuit. Only suppress when the user is
      // ALREADY on a DM call (callState !== 'idle') — stacking two
      // ringer surfaces on top of an active DM is unactionable.
      // Sitting in a voice channel is NOT busy: with the per-room
      // MediaContext refactor (1.1.18A) the two calls coexist with
      // independent mic, camera mutex, and shared denoiser — rings
      // through and the user decides.
      const dmBusy = useDmCallStore.getState().callState !== 'idle'
      if (dmBusy) {
        try {
          socket.emit('dm:call:decline', {
            dmChannelId: data.dmChannelId,
            callerId: data.callerId,
          })
        } catch { /* noop */ }
        return
      }

      setCallState('incoming', {
        dmId: data.dmChannelId,
        callType: data.callType || 'audio',
        callerId: data.callerId,
        callerName: data.callerName,
      })

      // In-app blue corner toast — browser only. In Electron the
      // OS-level notification (showCallNotification → custom-notif
      // fallback) is the single ringer surface, so we don't add a
      // second corner card inside the window.
      if (!isElectronEnv) {
        showToast({
          id: `call-${data.dmChannelId}`,
          type: 'call',
          title: `${data.callerName || 'Someone'} is calling`,
          body: `Incoming ${data.callType || 'audio'} call`,
          dmChannelId: data.dmChannelId,
          callerId: data.callerId,
          callType: data.callType,
        })
      }

      if (isElectronEnv) {
        // Electron: ONE surface, the frameless `incoming-call.html`
        // popup in the right-bottom corner of the OS desktop. It hosts
        // Accept/Decline buttons directly so the user can answer
        // without first navigating into the chat.
        try {
          ;(window as any).electronAPI?.incomingCall?.show?.({
            callerName: data.callerName || 'Someone',
            callType: data.callType || 'audio',
            dmChannelId: data.dmChannelId,
            callerId: data.callerId,
          })
        } catch { /* noop */ }
      } else {
        // Browser: rely on the OS Notification API. Clicking the toast
        // focuses the window + navigates to the DM where the in-app
        // TelegramCallOverlay handles accept/decline.
        showCallNotification({
          callerName: data.callerName || 'Someone',
          callType: data.callType || 'audio',
          dmChannelId: data.dmChannelId,
        })
      }

      // Ringtone only if not already in this DM call overlay
      if (!inThisDm) {
        sounds.ringtone()
      }
    }

    // Helper — close the floating Electron ringer window when a call
    // resolves on the wire (either side). No-op outside Electron.
    const dismissElectronRinger = () => {
      try {
        ;(window as any).electronAPI?.incomingCall?.dismiss?.()
      } catch { /* noop */ }
    }

    const handleCallEnded = (data: any) => {
      hideToast(`call-${data.dmChannelId}`)
      dismissCallNotification(data.dmChannelId)
      dismissElectronRinger()
      sounds.stopRingtone()
      sounds.stopOutgoingRing()
      sounds.callEndedByRemote()
      setCallState('idle')
    }

    const handleCallDeclined = (data: any) => {
      hideToast(`call-${data.dmChannelId}`)
      dismissCallNotification(data.dmChannelId)
      dismissElectronRinger()
      sounds.stopRingtone()
      sounds.stopOutgoingRing()
      sounds.callEndedByRemote()
      setCallState('idle')
    }

    const handleCallAccepted = (data: any) => {
      hideToast(`call-${data.dmChannelId}`)
      dismissCallNotification(data.dmChannelId)
      dismissElectronRinger()
      sounds.stopRingtone()
      sounds.stopOutgoingRing()
      sounds.callConnected()
      setCallState('connected')
    }

    const handleCallUnavailable = (data: any) => {
      sounds.stopOutgoingRing()
      sounds.callEndedByRemote()
      setCallState('idle')
      showToast({
        id: `call-unavailable-${data.dmChannelId}-${Date.now()}`,
        type: 'message',
        title: 'User unavailable',
        body: data.reason === 'offline'
          ? 'This user is offline right now.'
          : 'Cannot reach this user.',
        dmChannelId: data.dmChannelId,
      })
    }

    // Server fires this on OTHER tabs when the user accepts/declines a call
    // somewhere else — those tabs need to hide the ringer immediately.
    // Otherwise the user could accept on tab A, hang up, then later "accept"
    // again on tab B and join a room their other side already left.
    const handleResolvedElsewhere = (data: any) => {
      hideToast(`call-${data.dmChannelId}`)
      dismissCallNotification(data.dmChannelId)
      dismissElectronRinger()
      sounds.stopRingtone()
      setCallState('idle')
    }

    // OSPROD-88: incoming PSTN call routed to this user via Twilio.
    // Render the same call-toast surface as internal DM calls (Accept
    // / Decline buttons + ringtone). Accept connects livekitStore to
    // `dm-call-<sessionId>` where livekit-sip already parked the SIP
    // participant; Decline POSTs /telephony/calls/:id/end (RINGING →
    // MISSED; hangs up the Twilio leg).
    const handlePhoneIncoming = (data: any) => {
      if (data.direction === 'OUTBOUND') return
      const callerLabel = data.callerNumber || 'Phone caller'
      const isElectronEnv = typeof window !== 'undefined' && (
        window.location.protocol === 'file:' ||
        !!(window as any).electronAPI
      )

      setCallState('incoming', {
        dmId: data.dmChannelId,
        callType: 'audio',
        callerId: 'phone',
        callerName: callerLabel,
        // Ring-time session ids → dmCallStore stash (@os/messenger ≥2.0.46).
        // The mobile DmCallSplash and the in-thread overlay answer through
        // dmCallStore.acceptIncomingPhoneCall / declineIncomingPhoneCall,
        // which read these back — without the stash their Accept is a
        // guarded no-op. The toast below still carries its own copy:
        // per-toast ids survive a superseding ring, while this single-slot
        // stash intentionally tracks only the LATEST ring.
        incomingPhone: {
          sessionId: data.callSessionId,
          room: data.dmCallRoom,
          callerNumber: data.callerNumber ?? null,
        },
      })

      if (!isElectronEnv) {
        showToast({
          id: `phone-call-${data.callSessionId}`,
          type: 'call',
          title: callerLabel,
          body: 'Incoming phone call',
          dmChannelId: data.dmChannelId,
          callType: 'audio',
          phoneCallRoom: data.dmCallRoom,
          phoneCallSessionId: data.callSessionId,
          phoneCallerNumber: data.callerNumber,
        })
        sounds.ringtone()
        try {
          showCallNotification({
            callerName: callerLabel,
            callType: 'audio',
            dmChannelId: data.dmChannelId,
          })
        } catch { /* noop */ }
      } else {
        // Electron: ONE surface — the floating always-on-top ringer
        // (parity with the user-user path above; the in-app toast and
        // the OS notification are both suppressed). callerId:'phone'
        // marks the payload so the onResolved bridge below routes the
        // resolution through the dmCallStore PSTN actions instead of
        // the dm:call:accept/decline socket protocol — without it the
        // bridge's payload guard dropped PSTN clicks on the floor and
        // desktop users could not answer inbound phone calls at all.
        try {
          ;(window as any).electronAPI?.incomingCall?.show?.({
            callerName: callerLabel,
            callType: 'audio',
            dmChannelId: data.dmChannelId,
            callerId: 'phone',
            phoneCallSessionId: data.callSessionId,
          })
        } catch { /* noop */ }
        // The popup window carries no audio of its own — this renderer
        // plays the loop (stopped by accept/decline/terminal status).
        sounds.ringtone()
      }
    }

    // OSPROD-121: toast for new messages in the room-chat of the voice
    // channel the user is CURRENTLY in. Mirrors the DM-message toast
    // above (corner card + sound). We gate by voiceStore.currentChannelId
    // so a user who isn't in a call sees nothing — channel chat outside
    // a call already surfaces via the unread badge in the sidebar; this
    // toast is specifically for the in-call case where the user has the
    // video grid open and can miss a chat message tucked into the side
    // panel.
    const handleChannelMessage = (message: any) => {
      if (!message || message.authorId === currentUser?.id) return
      const voiceChannelId = useVoiceStore.getState().currentChannelId
      if (!voiceChannelId) return
      if (message.channelId !== voiceChannelId) return
      // Avoid double-noise: if the side-panel Chat tab is currently open
      // the user is already looking at the conversation. Skip the toast
      // for that case (still increments the unread counter via the
      // dedicated store path).
      const isChatTabOpen =
        useVoiceStore.getState().sidePanelTab === 'chat' &&
        !useVoiceStore.getState().isSidePanelCollapsed
      if (isChatTabOpen) return

      // Resolve channel + server name from the loaded server tree —
      // we already cache that in serverStore so the toast can render
      // `[Room Name]: Author` per ticket spec without a network round trip.
      const servers = useServerStore.getState().servers
      let channelName: string | null = null
      let serverName: string | null = null
      let sourceServerId: string | null = null
      for (const s of servers) {
        const direct = s.channels?.find((c) => c.id === voiceChannelId)
        if (direct) { channelName = direct.name; serverName = s.name; sourceServerId = s.id; break }
        for (const cat of s.categories || []) {
          const inCat = cat.channels?.find((c) => c.id === voiceChannelId)
          if (inCat) { channelName = inCat.name; serverName = s.name; sourceServerId = s.id; break }
        }
        if (channelName) break
      }

      const authorName = formatName(message.author)
      // OSPROD-152: sender goes on its OWN line (senderName) rather than
      // being crammed into the title as "[Room]: Author" — that single
      // truncated line dropped the author on longer room names, so users
      // saw the room + preview but not who sent it. Title now carries the
      // room (with server prefix); senderName carries the nickname.
      const title = channelName
        ? (serverName ? `[${serverName}] ${channelName}` : channelName)
        : authorName
      const body = message.content
        || (message.photoUrl ? 'Photo' : message.videoUrl ? 'Video' : message.audioUrl ? 'Voice message' : message.gifUrl ? 'GIF' : 'Message')

      showToast({
        id: `room-msg-${message.id}`,
        type: 'message',
        title,
        senderName: channelName ? authorName : undefined,
        body,
        // OSPROD-269: bind the reply destination to THIS toast at creation
        // — message.channelId, NOT a later read of voiceStore, so the
        // reply still hits the source room's chat even if the user hops
        // to another room/screen while the toast is up. The serverId is
        // the SOURCE server (may differ from the active one); the send
        // transport is channel-id-addressed so it doesn't matter for
        // delivery, but we keep it for future navigation affordances.
        replyTarget: { kind: 'channel', channelId: message.channelId, serverId: sourceServerId },
      })
      try { sounds.message() } catch { /* noop */ }
    }

    // Fired by the server when the PHONE side hangs up (SIP BYE →
    // /api/sip/participant-status webhook) or when the 30s missed-timeout
    // fires, so the overlay closes immediately rather than waiting for
    // the LiveKit participant-left fallback event.
    const handlePhoneStatus = (data: any) => {
      if (!data?.callSessionId) return
      const st = useDmCallStore.getState()
      const isOurActiveCall = st.phoneCallSessionId === data.callSessionId

      // Outbound dial answered (the server-side outbound watcher saw
      // sip.callStatus go active): flip the dialing tab's overlay from
      // 'outgoing' to 'connected' so the elapsed timer starts at the
      // ANSWER moment, not at room join.
      if (data.status === 'CONNECTED') {
        if (isOurActiveCall && st.callState === 'outgoing') {
          st.setCallState('connected', { callType: 'audio' })
        }
        return
      }

      // For any terminal/dismissal status: always hide the ringing toast
      // and stop the ringtone (both are no-ops if absent/already stopped).
      // Non-accepting instances (other tabs, the mobile shell's full-screen
      // DmCallSplash) sit in callState='incoming' — reset them to idle on
      // EVERY terminal status (declined on another tab, missed timeout,
      // caller hangup — not just answered-elsewhere) or the splash outlives
      // the call. The !isOurActiveCall guard keeps the ACCEPTING instance's
      // live call (it has phoneCallSessionId set) out of this reset.
      if (
        data.status === 'MISSED' ||
        data.status === 'ENDED' ||
        data.status === 'FAILED' ||
        data.status === 'ANSWERED_ELSEWHERE'
      ) {
        hideToast(`phone-call-${data.callSessionId}`)
        // Electron: the floating ringer popup is this desktop's only ring
        // surface — close it on every terminal status (same unconditional
        // dismiss the user-user dm:call:* handlers use; no-op when absent
        // or already closed by the user's own Accept/Decline click).
        dismissElectronRinger()
        try { sounds.stopRingtone() } catch { /* noop */ }
        if (!isOurActiveCall && st.callState === 'incoming' && st.callerId === 'phone') {
          st.setCallState('idle')
        }
      }

      // ANSWERED_ELSEWHERE: another tab/device accepted — dismissal only;
      // nothing further to tear down on this instance.
      if (data.status === 'ANSWERED_ELSEWHERE') {
        return
      }

      // MISSED/ENDED/FAILED: also tear down the active call on this tab
      // if it matches ours (e.g. phone side hung up).
      if (
        isOurActiveCall &&
        (data.status === 'MISSED' || data.status === 'ENDED' || data.status === 'FAILED')
      ) {
        // Clear the sid FIRST so disconnect() doesn't fire a redundant
        // /end for a session the phone side already closed.
        st.setPhoneCallSessionId(null)
        st.disconnect()
        st.setCallState('idle')
      }
    }

    // Unified phone lobby: a PSTN caller parked in a voice channel's waiting
    // room. Per user spec (2026-06-11): the toast + sound fire ONLY when this
    // client is currently CONNECTED to the channel being called — hosts
    // elsewhere rely on the left-rail lobby strip (DM phone calls keep their
    // full ring via phone:call:incoming, untouched). The sound is a single
    // knock, not the looping ringtone: a loop squelches the host's live mic
    // ~0.5s every 1.5s for up to 60s mid-conversation.
    const handleWaitingRequest = (data: any) => {
      if (typeof data?.userId !== 'string' || !data.userId.startsWith('phone:')) return
      if (!data.waitingId || !data.channelId) return
      if (useVoiceStore.getState().currentChannelId !== data.channelId) return
      showToast({
        id: `phone-waiting-${data.waitingId}`,
        type: 'call',
        title: data.channelName ? `Caller for ${data.channelName}` : 'Incoming caller',
        senderName: data.username || formatPhoneDisplay(data.userId.slice('phone:'.length)),
        body: 'Wants to join the voice channel',
        phoneWaiting: {
          channelId: data.channelId,
          serverId: data.serverId ?? null,
          waitingId: data.waitingId,
        },
      })
      sounds.knock()
    }
    // Any resolution (approve/reject from any surface or host, or the 60s
    // EXPIRED timeout) hides the toast everywhere.
    const handleWaitingResolved = (data: any) => {
      hideToast(`phone-waiting-${data?.waitingId}`)
    }

    socket.on('message:new', handleChannelMessage)
    socket.on('dm:message:new', handleDmMessage)
    socket.on('dm:call:incoming', handleIncomingCall)
    socket.on('dm:call:ended', handleCallEnded)
    socket.on('dm:call:declined', handleCallDeclined)
    socket.on('dm:call:accepted', handleCallAccepted)
    socket.on('dm:call:unavailable', handleCallUnavailable)
    socket.on('dm:call:resolved-elsewhere', handleResolvedElsewhere)
    socket.on('phone:call:incoming', handlePhoneIncoming)
    socket.on('phone:call:status', handlePhoneStatus)
    socket.on('voice:waiting:request', handleWaitingRequest)
    socket.on('voice:waiting:resolved', handleWaitingResolved)

    // Bridge for the always-on-top Electron ringer: when the user
    // clicks Accept or Decline in the floating popup, run the same
    // logic the in-app toast does (emit dm:call:accept|decline,
    // navigate, sound, state). The unsubscribe is returned by the
    // preload bridge so we can detach on unmount.
    let unsubscribeRinger: (() => void) | null = null
    try {
      const bridge = (window as any).electronAPI?.incomingCall
      if (bridge && typeof bridge.onResolved === 'function') {
        unsubscribeRinger = bridge.onResolved(({ action, payload }: { action: 'accept' | 'decline'; payload: any }) => {
          // PSTN ring (OSPROD-88): no internal user on the other side, so
          // the dm:call:accept/decline socket protocol below cannot resolve
          // it. handlePhoneIncoming marks the popup payload with
          // callerId:'phone'; resolution runs through the shared dmCallStore
          // actions (@os/messenger ≥2.0.46) — the same protocol as the
          // in-app toast / mobile splash. Those actions read the single-slot
          // ring stash, which always mirrors the LATEST ring — exactly what
          // the (equally single-slot) popup is showing. They also hide the
          // ring toast + stop the ringtone themselves; the extra stop here
          // covers the no-username/stale-stash early-outs.
          if (payload?.callerId === 'phone') {
            sounds.stopRingtone()
            if (action === 'accept') {
              const username = useAuthStore.getState().user?.username
              if (!username) return
              void useDmCallStore.getState().acceptIncomingPhoneCall(username)
              try {
                const route = `/channels/dm-${payload.dmChannelId}`
                if (payload.dmChannelId && window.location.pathname !== route) {
                  window.history.pushState({}, '', route)
                  window.dispatchEvent(new PopStateEvent('popstate'))
                }
              } catch { /* noop */ }
            } else {
              useDmCallStore.getState().declineIncomingPhoneCall()
            }
            return
          }
          if (!payload?.dmChannelId || !payload?.callerId) return
          hideToast(`call-${payload.dmChannelId}`)
          sounds.stopRingtone()
          if (action === 'accept') {
            setCallState('connected', {
              dmId: payload.dmChannelId,
              callType: payload.callType || 'audio',
            })
            socket.emit('dm:call:accept', {
              dmChannelId: payload.dmChannelId,
              callerId: payload.callerId,
            })
            try {
              const route = `/channels/dm-${payload.dmChannelId}`
              if (window.location.pathname !== route) {
                window.history.pushState({}, '', route)
                window.dispatchEvent(new PopStateEvent('popstate'))
              }
            } catch { /* noop */ }
          } else {
            setCallState('idle')
            socket.emit('dm:call:decline', {
              dmChannelId: payload.dmChannelId,
              callerId: payload.callerId,
            })
          }
        })
      }
    } catch { /* non-Electron, noop */ }

    return () => {
      socket.off('message:new', handleChannelMessage)
      socket.off('dm:message:new', handleDmMessage)
      socket.off('dm:call:incoming', handleIncomingCall)
      socket.off('dm:call:ended', handleCallEnded)
      socket.off('dm:call:declined', handleCallDeclined)
      socket.off('dm:call:accepted', handleCallAccepted)
      socket.off('dm:call:unavailable', handleCallUnavailable)
      socket.off('dm:call:resolved-elsewhere', handleResolvedElsewhere)
      socket.off('phone:call:incoming', handlePhoneIncoming)
      socket.off('phone:call:status', handlePhoneStatus)
      socket.off('voice:waiting:request', handleWaitingRequest)
      socket.off('voice:waiting:resolved', handleWaitingResolved)
      if (unsubscribeRinger) unsubscribeRinger()
    }
  }, [showToast, hideToast, currentUser?.id, setCallState])

  // Auto-hide lives per-item inside ToastItemComponent (OSPROD-269) so a
  // toast with a quick reply being typed can pause its own timer without
  // freezing the rest of the stack.

  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
      {toasts.map((toast) => (
        <ToastItemComponent key={toast.id} toast={toast} />
      ))}
    </div>
  )
}
